import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import {
    Badge,
    Button,
    CollapsibleSection,
    LoaderPage,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@freelancelabs/components';
import { ArrowLeft } from '@freelancelabs/icons';

import Card from '../../components/Card';
import { MatrixSkeleton } from '../../components/Skeletons';
import { DataScope } from '../../graphql/__generated__/schema';
import {
    PermissionsDocument,
    PortalModulesDocument,
    PortalUserDocument,
    PortalUserQuery,
    RolesDocument,
    ViewerDocument,
} from '../../graphql/__generated__/types';
import RightsMatrix from '../roles/RightsMatrix';
import ChangeRoleDialog from './ChangeRoleDialog';
import DashboardsCard from './DashboardsCard';
import DeleteUserDialog from './DeleteUserDialog';
import EditPartnerListDialog from './EditPartnerListDialog';
import EditPerimeterDialog from './EditPerimeterDialog';
import ReinstateMembershipDialog from './ReinstateMembershipDialog';
import ResendInvitationDialog, { resendButtonLabel } from './ResendInvitationDialog';
import RevokeMembershipDialog from './RevokeMembershipDialog';
import { EMPTY, dataScopeLabel, formatDate, formatDateTime, groupPerimeter, mfaLabel } from './display';
import { invitationChaseSentence, invitationSentence, invitationStateOf, resendBlocker } from './invitation';
import {
    closureAuthorSentence,
    closureIsHistorySentence,
    closureReasonSentence,
    closureStateOf,
    reinstateOfferOf,
    revokeOfferOf,
} from './revocation';
import { statusCategory, statusLabel } from './status';

type Membership = NonNullable<NonNullable<PortalUserQuery['portalUser']>['memberships']>[number];

/**
 * The sheet of one portal user. It describes the person, not one membership: a person may hold
 * several memberships inside their client's tree, each with its own role, status and perimeter,
 * and the memberships shown are bounded by the accounts the actor may see. Per-membership gestures
 * (change role, change perimeter, close/reopen access, grant/withdraw dashboard) act on one
 * precise membership. Sections the API does not feed are named rather than hidden.
 */
const UserDetailPage = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const [resending, setResending] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // These gestures are held by membership id, not the object: the object would render the row
    // as it was before the refetch.
    const [reinstating, setReinstating] = useState<string | null>(null);
    const [changingRole, setChangingRole] = useState<string | null>(null);
    const [editingPerimeter, setEditingPerimeter] = useState<string | null>(null);
    const [revoking, setRevoking] = useState<string | null>(null);
    const [editingPartnerList, setEditingPartnerList] = useState(false);
    // Open perimeter sections, keyed « membershipId:group ». Collapsed at first.
    const [openPerimeter, setOpenPerimeter] = useState<Set<string>>(new Set());
    const togglePerimeter = (key: string, open: boolean) =>
        setOpenPerimeter((previous) => {
            const next = new Set(previous);
            if (open) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });
    const { data, loading, error, refetch } = useQuery(PortalUserDocument, {
        variables: { userId: userId ?? '' },
        skip: !userId,
    });
    const { data: rolesData } = useQuery(RolesDocument);
    // The rights matrix needs both referentials: the rights, and the modules that name and order
    // its groups; otherwise every module right files under « module inconnu ».
    const {
        data: referentialData,
        loading: referentialLoading,
        error: referentialError,
    } = useQuery(PermissionsDocument);
    const { data: modulesData, loading: modulesLoading, error: modulesError } = useQuery(PortalModulesDocument);
    // Viewer: to hide « Delete » on the actor's own sheet (the API refuses it too).
    const { data: viewerData } = useQuery(ViewerDocument);

    const backToUsers = (
        <Link to="/utilisateurs" className="link inline-flex items-center gap-1.5 text-sm">
            <ArrowLeft size={14} />
            {c('Users').t`Users`}
        </Link>
    );

    // Initial load only, never a background refetch: `loading` goes true on every
    // refetchQueries this sheet's gestures trigger, and rendering the loader then would unmount an
    // open modal along with its useMutation state. A sheet that already has data does not blank out.
    if (loading && !data) {
        return <LoaderPage />;
    }

    const user = data?.portalUser;

    // A failed query is not an absent user: saying « not found » when the query broke sends the
    // reader looking for a person who is right there. The message carries what the server said.
    if (error) {
        return (
            <section className="flex flex-col gap-3">
                {backToUsers}
                <h1 className="text-xl font-semibold text-text-900">{c('Users').t`This sheet could not be loaded`}</h1>
                <p className="text-text-600">{c('Users').t`The server refused the request.`}</p>
                <p className="max-w-3xl rounded-md border border-border-norm bg-background-weak p-3 font-mono text-xs text-text-600">
                    {error.message}
                </p>
            </section>
        );
    }

    // Out of perimeter and non-existent give the same answer, by design of the API:
    // the screen must not say more than the query does.
    if (!user) {
        return (
            <section className="flex flex-col gap-3">
                {backToUsers}
                <h1 className="text-xl font-semibold text-text-900">{c('Users').t`User not found`}</h1>
                <p className="text-text-600">
                    {c('Users').t`This user does not exist, or holds no membership on an account you can see.`}
                </p>
            </section>
        );
    }

    const memberships = (user.memberships ?? []).filter(Boolean) as Membership[];
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || (user.email ?? EMPTY);
    // Zero (an assigned but empty list) is distinct from a null partnerList (none assigned).
    const partnerCount = user.partnerList?.partnerCount ?? 0;
    /** Both lists summed: what a restricted perimeter retains. */
    const restrictedCount = (membership: Membership) =>
        (membership.visibleNeedCount ?? 0) + (membership.visibleServiceCount ?? 0);

    const roles = [...(rolesData?.portal ?? []), ...(rolesData?.backoffice ?? [])].filter(Boolean);
    // Only portal roles are offered on a client membership: a back-office role refuses.
    const portalRoles = (rolesData?.portal ?? []).filter(Boolean);
    const referential = (referentialData?.permissions ?? []).filter(Boolean);
    const modules = modulesData?.portalModules ?? [];
    const matrixLoading = referentialLoading || modulesLoading;
    const matrixError = referentialError ?? modulesError;
    // The distinct roles this person holds; several is a common case.
    const heldRoleIds = [...new Set(memberships.map((membership) => membership.roleId))];
    // A list, because a person may be known by several source systems, and empty when the account
    // was created by hand. The business reference is added beside the technical id, never instead.
    const externalReferences = (user.externalReferences ?? [])
        .filter(Boolean)
        .map((reference) => {
            const anchor = `${reference.externalId} (${reference.sourceSystem})`;
            return reference.businessReference ? `${anchor} · ${reference.businessReference}` : anchor;
        })
        .join(' · ');

    // Both read from the same row, so the card and its button cannot contradict each other.
    const invitationState = invitationStateOf(user);
    const chaseSentence = invitationChaseSentence(invitationState);
    const blocker = resendBlocker(user);
    // A pending invitation is the only thing that fills invitationExpiresAt: a live link exists.
    const hasLiveLink = Boolean(user.invitationExpiresAt);

    // Each closure is read against its membership's status: the `revoked*` fields describe the last
    // closure and are not cleared by a reopening, so rendering them raw would announce a closure
    // that has been undone. `closureStateOf` is the single place that reading happens.
    const closures = memberships
        .map((membership) => ({ membership, closure: closureStateOf(membership) }))
        .filter((entry) => entry.closure.kind !== 'none')
        // What is closed now comes first: it is the only part carrying a gesture.
        .sort((first, second) => Number(second.closure.kind === 'current') - Number(first.closure.kind === 'current'));

    const reinstatingEntry = closures.find((entry) => entry.membership.membershipId === reinstating);
    const changingRoleMembership = memberships.find((membership) => membership.membershipId === changingRole);
    const editingPerimeterMembership = memberships.find((membership) => membership.membershipId === editingPerimeter);
    const revokingMembership = memberships.find((membership) => membership.membershipId === revoking);

    return (
        <section className="flex flex-col gap-5">
            {backToUsers}

            <header className="flex flex-wrap items-start gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-900">{fullName}</h1>
                    <p className="mt-1 text-xs text-text-600">
                        {user.function ? `${user.function} · ` : ''}
                        <span className="font-mono text-xs">{user.email}</span>
                    </p>
                </div>
                {user.disabledAt && (
                    <Badge category="default" size="small">
                        {c('Users').t`Account disabled`}
                    </Badge>
                )}
                {/* Hidden on the actor's own sheet; shown while the viewer is unknown rather than
                    flickering. */}
                {viewerData?.viewer?.id !== user.userId && (
                    <Button className="ml-auto" category="destructive" onClick={() => setDeleting(true)}>
                        {c('Delete user').t`Delete the user`}
                    </Button>
                )}
            </header>

            <Card title={c('Users').t`Identity and status`}>
                <dl className="grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                    <Field label={c('Users column').t`Last name`} value={user.lastName ?? EMPTY} />
                    <Field label={c('Users column').t`First name`} value={user.firstName ?? EMPTY} />
                    <Field label={c('Users column').t`Email`} value={user.email ?? EMPTY} mono />
                    <Field label={c('Users').t`Job title`} value={user.function ?? EMPTY} />
                    {/* « Activity », not « login »: the API measures a request bearing a still-valid
                        token, rounded to the hour, so the two can drift apart. */}
                    <Field label={c('Users').t`Last activity`} value={formatDateTime(user.lastLoginAt)} mono />
                    <Field label={c('Users column').t`Invited on`} value={formatDate(user.invitedAt)} mono />
                    <Field
                        label={c('Users column').t`Account created`}
                        value={user.activatedAt ? formatDate(user.activatedAt) : c('Users').t`Awaiting activation`}
                        mono={Boolean(user.activatedAt)}
                    />
                    {/* The state comes from the pool (three cases: factors, no factor, and « not read »
                        when the pool did not answer); « no factor » does not mean « non-compliant ». */}
                    <Field
                        label={c('Users').t`Two-factor authentication`}
                        value={mfaLabel(user.mfa, user.mfaEnabledAt)}
                        mono={Boolean(user.mfaEnabledAt)}
                    />
                    {user.disabledReason && (
                        <Field label={c('Users').t`Reason for deactivation`} value={user.disabledReason} />
                    )}
                    <Field
                        label={c('Users').t`External references`}
                        value={externalReferences || EMPTY}
                        mono={Boolean(externalReferences)}
                    />
                </dl>
            </Card>

            {/* Rendered whenever the person is not in yet, including when the button is refused:
                the card is where the state becomes legible. */}
            {invitationState.kind !== 'none' && (
                <Card title={c('Users').t`Invitation`}>
                    <p className="text-sm text-text-900">{invitationSentence(invitationState)}</p>
                    {chaseSentence && <p className="mt-2 text-xs text-text-600">{chaseSentence}</p>}
                    <dl className="mt-4 grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                        {/* Not rendered when no link exists, where it would read as a date that
                            failed to load. */}
                        {invitationState.kind !== 'missing' && (
                            <Field
                                label={c('Users').t`Invitation valid until`}
                                value={formatDate(user.invitationExpiresAt)}
                                mono
                            />
                        )}
                        <Field label={c('Users').t`Invitations sent`} value={String(invitationState.sentCount)} mono />
                    </dl>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <Button
                            category="default"
                            disabled={Boolean(blocker)}
                            title={blocker ?? undefined}
                            onClick={() => setResending(true)}
                        >
                            {resendButtonLabel(invitationState.sentCount)}
                        </Button>
                        {blocker && <p className="text-xs text-text-600">{blocker}</p>}
                    </div>
                </Card>
            )}

            {resending && user.userId && (
                <ResendInvitationDialog
                    userId={user.userId}
                    email={user.email ?? ''}
                    hasLiveLink={hasLiveLink}
                    onClose={() => setResending(false)}
                />
            )}

            {reinstatingEntry?.membership.membershipId && (
                <ReinstateMembershipDialog
                    membershipId={reinstatingEntry.membership.membershipId}
                    organizationName={reinstatingEntry.membership.organizationName ?? EMPTY}
                    roleName={reinstatingEntry.membership.roleName ?? EMPTY}
                    closure={reinstatingEntry.closure}
                    onClose={() => setReinstating(null)}
                    onResendInvitation={() => {
                        setReinstating(null);
                        setResending(true);
                    }}
                />
            )}

            {changingRoleMembership?.membershipId && (
                <ChangeRoleDialog
                    membershipId={changingRoleMembership.membershipId}
                    organizationName={changingRoleMembership.organizationName ?? EMPTY}
                    personName={fullName}
                    currentRoleId={changingRoleMembership.roleId ?? null}
                    currentRoleName={changingRoleMembership.roleName ?? EMPTY}
                    roles={portalRoles}
                    onClose={() => setChangingRole(null)}
                />
            )}

            {deleting && user.userId && (
                <DeleteUserDialog
                    user={{ id: user.userId, name: fullName, email: user.email ?? EMPTY }}
                    onClose={() => setDeleting(false)}
                    onDeleted={() => navigate('/utilisateurs')}
                />
            )}

            {editingPerimeterMembership?.membershipId && (
                <EditPerimeterDialog
                    membershipId={editingPerimeterMembership.membershipId}
                    accountName={editingPerimeterMembership.organizationName ?? EMPTY}
                    onClose={() => setEditingPerimeter(null)}
                    onSaved={() => refetch()}
                />
            )}

            {revokingMembership?.membershipId && (
                <RevokeMembershipDialog
                    membershipId={revokingMembership.membershipId}
                    organizationName={revokingMembership.organizationName ?? EMPTY}
                    roleName={revokingMembership.roleName ?? EMPTY}
                    personName={fullName}
                    onClose={() => setRevoking(null)}
                />
            )}

            {editingPartnerList && user.userId && (
                <EditPartnerListDialog
                    userId={user.userId}
                    currentListId={user.partnerList?.id ?? null}
                    onClose={() => setEditingPartnerList(false)}
                    onSaved={() => refetch()}
                />
            )}

            <Card
                title={c('Users').t`Accounts`}
                counter={memberships.length}
                actions={
                    // Add an account: replays the invitation journey seeded with the person's email.
                    user.email ? (
                        <Button
                            category="weak"
                            size="small"
                            onClick={() =>
                                navigate('/utilisateurs/nouveau', { state: { addAccountEmail: user.email } })
                            }
                        >
                            {c('Users').t`Add an account`}
                        </Button>
                    ) : undefined
                }
            >
                <div className="fl-table-container" tabIndex={0} role="region" aria-label={c('Users').t`Accounts`}>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border-norm text-left text-xs uppercase tracking-wide text-text-500">
                                <th className="py-2 pr-4 font-semibold">{c('Users column').t`Account`}</th>
                                <th className="py-2 pr-4 font-semibold">{c('Users column').t`Role`}</th>
                                <th className="py-2 pr-4 font-semibold">{c('Users column').t`Status`}</th>
                                <th className="py-2 pr-4 font-semibold">{c('Users').t`Perimeter`}</th>
                                <th className="py-2 font-semibold">{c('Users').t`Joined on`}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {memberships.map((membership) => (
                                <tr key={membership.membershipId} className="border-b border-border-weak">
                                    <td className="py-2.5 pr-4">
                                        <span className="text-text-900">{membership.organizationName}</span>
                                        {membership.isDefault && (
                                            <span className="ml-2 text-xs text-text-500">
                                                {c('Users').t`(opened at sign-in)`}
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2.5 pr-4">
                                        <div className="flex flex-col items-start gap-1">
                                            <span className="text-text-900">{membership.roleName}</span>
                                            {membership.membershipId && (
                                                <Button
                                                    category="weak"
                                                    size="small"
                                                    onClick={() => setChangingRole(membership.membershipId ?? null)}
                                                >
                                                    {c('Users').t`Change the role`}
                                                </Button>
                                            )}
                                            {/* Close the access: offered while the membership is open;
                                                reopening lives in the Closures card. */}
                                            {membership.membershipId &&
                                                revokeOfferOf(membership, closureStateOf(membership)) && (
                                                    <Button
                                                        category="weak"
                                                        size="small"
                                                        onClick={() => setRevoking(membership.membershipId ?? null)}
                                                    >
                                                        {c('Users').t`Close the access`}
                                                    </Button>
                                                )}
                                        </div>
                                    </td>
                                    <td className="py-2.5 pr-4">
                                        {membership.status && (
                                            <Badge category={statusCategory(membership.status)} size="small">
                                                {statusLabel(membership.status)}
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="py-2.5 pr-4">
                                        {dataScopeLabel(membership.dataScope, restrictedCount(membership))}
                                    </td>
                                    <td className="py-2.5 font-mono text-xs">
                                        {formatDate(membership.joinedAt ?? membership.createdAt)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Rendered as soon as any closure is on record, including undone ones: a closure
                survives its reopening. Each entry says which of the two it is. */}
            {closures.length > 0 && (
                <Card title={c('Users').t`Closures`}>
                    <div className="flex flex-col gap-5">
                        {closures.map(({ membership, closure }) => {
                            const offer = reinstateOfferOf(membership, closure);
                            const historySentence = closureIsHistorySentence(closure, membership);

                            return (
                                <div key={membership.membershipId} className="flex flex-col gap-1.5">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="text-sm font-semibold text-text-900">
                                            {membership.organizationName}
                                        </h3>
                                        {membership.status && (
                                            <Badge category={statusCategory(membership.status)} size="small">
                                                {statusLabel(membership.status)}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-sm text-text-900">{closureAuthorSentence(closure)}</p>
                                    <p className="text-xs text-text-600">{closureReasonSentence(closure)}</p>
                                    {historySentence && <p className="text-sm text-text-500">{historySentence}</p>}
                                    {offer.offered && (
                                        <div className="mt-1 flex flex-wrap items-center gap-3">
                                            <Button
                                                category="default"
                                                size="small"
                                                disabled={Boolean(offer.blocker)}
                                                title={offer.blocker ?? undefined}
                                                onClick={() => setReinstating(membership.membershipId ?? null)}
                                            >
                                                {c('Users').t`Reopen this access`}
                                            </Button>
                                            {offer.blocker && <p className="text-xs text-text-600">{offer.blocker}</p>}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Card>
            )}

            <Card title={c('Users').t`Perimeter`}>
                <div className="flex flex-col gap-4">
                    {memberships.map((membership) => (
                        <div key={membership.membershipId}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h3 className="text-sm font-semibold text-text-900">{membership.organizationName}</h3>
                                {membership.membershipId && (
                                    <Button
                                        category="weak"
                                        size="small"
                                        onClick={() => setEditingPerimeter(membership.membershipId ?? null)}
                                    >
                                        {c('Users').t`Change the perimeter`}
                                    </Button>
                                )}
                            </div>
                            {membership.dataScope === DataScope.Restricted ? (
                                (membership.visibleActivities ?? []).length > 0 ? (
                                    <div className="mt-2 flex flex-col gap-2">
                                        {groupPerimeter(membership.visibleActivities ?? []).map((group) => {
                                            const sectionKey = `${membership.membershipId}:${group.key}`;
                                            return (
                                                <CollapsibleSection
                                                    key={sectionKey}
                                                    className="fl-collapsible-section--group fl-collapsible-section--white-head"
                                                    open={openPerimeter.has(sectionKey)}
                                                    onOpenChange={(open) => togglePerimeter(sectionKey, open)}
                                                    title={group.label}
                                                    count={
                                                        <Badge category="default" size="small">
                                                            {String(group.activities.length)}
                                                        </Badge>
                                                    }
                                                >
                                                    <Table>
                                                        <TableBody>
                                                            {group.activities.map((activity) => (
                                                                <TableRow key={activity.id}>
                                                                    <TableCell>{activity.name ?? EMPTY}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </CollapsibleSection>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    // The view reads `membership.status`: an invited person sees
                                    // nothing yet, whatever was configured for them.
                                    <p className="mt-1 text-xs text-text-600">
                                        {c('Users')
                                            .t`Restricted perimeter, nothing visible yet: the activities open when the membership becomes active.`}
                                    </p>
                                )
                            ) : (
                                <p className="mt-1 text-xs text-text-600">
                                    {c('Users')
                                        .t`No personal restriction: the person sees the whole account, including what is added later.`}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </Card>

            <DashboardsCard memberships={memberships} personName={fullName} />

            <Card title={c('Users').t`Rights`}>
                {matrixError ? (
                    <p className="text-sm text-danger-600" role="alert">
                        {c('Users').t`The rights referential could not be loaded.`}
                    </p>
                ) : matrixLoading ? (
                    <MatrixSkeleton />
                ) : (
                    <div className="flex flex-col gap-6">
                        {heldRoleIds.map((roleId) => {
                            const role = roles.find((candidate) => candidate.id === roleId);
                            const accounts = memberships
                                .filter((membership) => membership.roleId === roleId)
                                .map((membership) => membership.organizationName)
                                .join(', ');
                            const granted = new Set(
                                (role?.permissions ?? []).map((permission) => permission?.code ?? '')
                            );

                            return (
                                <div key={roleId}>
                                    <div className="mb-3 flex flex-wrap items-baseline gap-2">
                                        <h3 className="text-sm font-semibold text-text-900">
                                            {role?.name ??
                                                memberships.find((membership) => membership.roleId === roleId)
                                                    ?.roleName}
                                        </h3>
                                        {memberships.length > 1 && (
                                            <span className="text-xs text-text-600">{accounts}</span>
                                        )}
                                        {role && (
                                            <Link to={`/roles/${role.id}`} className="link text-xs">
                                                {c('Users').t`See the role`}
                                            </Link>
                                        )}
                                    </div>
                                    <RightsMatrix referential={referential} modules={modules} granted={granted} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* Rendered per account; null is the ordinary case. */}
            <Card title={c('Users').t`Referring account manager`}>
                <div className="flex flex-col gap-4">
                    {memberships.map((membership) => (
                        <div key={membership.membershipId}>
                            <h3 className="text-sm font-semibold text-text-900">{membership.organizationName}</h3>
                            {membership.accountManager ? (
                                <div className="mt-1 flex flex-col gap-0.5">
                                    <span className="text-sm text-text-900">
                                        {membership.accountManager.displayName}
                                        {membership.accountManager.title && (
                                            <span className="ml-2 text-xs text-text-500">
                                                {membership.accountManager.title}
                                            </span>
                                        )}
                                    </span>
                                    {membership.accountManager.email && (
                                        <a className="link text-xs" href={`mailto:${membership.accountManager.email}`}>
                                            {membership.accountManager.email}
                                        </a>
                                    )}
                                </div>
                            ) : (
                                <p className="mt-1 text-xs text-text-600">
                                    {c('Users').t`No referring account manager resolves on this account.`}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </Card>

            {/* A zero count (empty list) is distinct from a null list (none assigned). */}
            <Card
                title={c('Users').t`Partner list`}
                actions={
                    <Button category="weak" size="small" onClick={() => setEditingPartnerList(true)}>
                        {c('Users').t`Change the partner list`}
                    </Button>
                }
            >
                {user.partnerList ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-text-900">{user.partnerList.name ?? EMPTY}</span>
                        {user.partnerList.isAnonymous && (
                            <Badge category="default" size="small">
                                {c('Users').t`Anonymous`}
                            </Badge>
                        )}
                        <span className="text-xs text-text-600">
                            {c('Users').ngettext(
                                msgid`${partnerCount} partner`,
                                `${partnerCount} partners`,
                                partnerCount
                            )}
                        </span>
                    </div>
                ) : (
                    <p className="text-xs text-text-600">{c('Users').t`No partner list is assigned to this person.`}</p>
                )}
            </Card>
        </section>
    );
};

interface FieldProps {
    label: string;
    value: string;
    mono?: boolean;
}

const Field = ({ label, value, mono }: FieldProps) => (
    <div className="min-w-0">
        <dt className="text-xs uppercase tracking-wide text-text-500">{label}</dt>
        <dd className={`mt-0.5 break-words text-text-900 ${mono ? 'font-mono text-xs' : 'text-sm'}`}>{value}</dd>
    </div>
);

export default UserDetailPage;
